"use client";

import { useState } from "react";
import type { BalanceGame, BalanceGameQuestion } from "@test-platform/shared/balance-game";

interface ChoiceResult {
  optionAPct: number;
  optionBPct: number;
  optionACount: number;
  optionBCount: number;
}

interface BalanceGameRunnerProps {
  game: BalanceGame;
}

export function BalanceGameRunner({ game }: BalanceGameRunnerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userChoices, setUserChoices] = useState<Record<string, "optionA" | "optionB">>({});
  const [choiceResults, setChoiceResults] = useState<Record<string, ChoiceResult>>({});
  const [selectedOption, setSelectedOption] = useState<"optionA" | "optionB" | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const currentQuestion: BalanceGameQuestion | undefined = game.questions[currentIndex];

  const handleSelect = async (choice: "optionA" | "optionB") => {
    if (selectedOption || isSubmitting || !currentQuestion) return;

    setSelectedOption(choice);
    setIsSubmitting(true);

    const qId = currentQuestion.id;
    setUserChoices((prev) => ({ ...prev, [qId]: choice }));

    try {
      const res = await fetch("/api/balance-game/choices", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          gameId: game.id,
          questionId: qId,
          choice,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setChoiceResults((prev) => ({
          ...prev,
          [qId]: {
            optionAPct: data.optionAPct,
            optionBPct: data.optionBPct,
            optionACount: data.optionACount,
            optionBCount: data.optionBCount,
          },
        }));
      } else {
        // Fallback default statistics on error
        setChoiceResults((prev) => ({
          ...prev,
          [qId]: { optionAPct: 50, optionBPct: 50, optionACount: 1, optionBCount: 1 },
        }));
      }
    } catch {
      setChoiceResults((prev) => ({
        ...prev,
        [qId]: { optionAPct: 50, optionBPct: 50, optionACount: 1, optionBCount: 1 },
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentIndex + 1 < game.questions.length) {
      setCurrentIndex((prev) => prev + 1);
      setSelectedOption(null);
    } else {
      setIsCompleted(true);
    }
  };

  if (isCompleted) {
    let majorityCount = 0;
    game.questions.forEach((q) => {
      const userChoice = userChoices[q.id];
      const result = choiceResults[q.id];
      if (userChoice && result) {
        const isA = userChoice === "optionA";
        if (isA && result.optionAPct >= 50) majorityCount++;
        if (!isA && result.optionBPct >= 50) majorityCount++;
      }
    });

    const majorityPct = Math.round((majorityCount / game.questions.length) * 100);

    return (
      <div className="mx-auto max-w-lg rounded-2xl bg-white p-6 shadow-xl border border-gray-100 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">결과 리포트</h2>
        <p className="text-gray-600 mb-6">{game.title} 완주!</p>

        <div className="my-6 rounded-xl bg-blue-50 p-6 border border-blue-100">
          <span className="text-sm font-semibold text-blue-600 uppercase tracking-wider">
            선택 성향 분석
          </span>
          <p className="mt-2 text-3xl font-extrabold text-blue-900">
            {majorityPct >= 50 ? `다수파 (${majorityPct}%)` : `소수파 (${100 - majorityPct}%)`}
          </p>
          <p className="mt-1 text-sm text-gray-600">
            {majorityPct >= 50
              ? "대중의 취향과 일치하는 안정적인 선택을 선호하시네요!"
              : "남들과 다른 나만의 뚜렷한 취향을 지닌 개성파시네요!"}
          </p>
        </div>

        <button
          onClick={() => {
            setCurrentIndex(0);
            setUserChoices({});
            setChoiceResults({});
            setSelectedOption(null);
            setIsCompleted(false);
          }}
          className="w-full rounded-xl bg-blue-600 py-3.5 text-white font-semibold hover:bg-blue-700 transition"
        >
          다시하기
        </button>
      </div>
    );
  }

  if (!currentQuestion) return null;

  const currentResult = choiceResults[currentQuestion.id];

  return (
    <div className="mx-auto max-w-lg rounded-2xl bg-white p-6 shadow-xl border border-gray-100">
      <div className="mb-4 flex items-center justify-between text-sm font-semibold text-gray-500">
        <span>{game.title}</span>
        <span>
          {currentIndex + 1} / {game.questions.length}
        </span>
      </div>

      <h3 className="mb-6 text-xl font-bold text-gray-900 text-center">
        {currentQuestion.title ?? "당신의 선택은?"}
      </h3>

      <div className="space-y-4">
        {/* Option A */}
        <button
          onClick={() => handleSelect("optionA")}
          disabled={Boolean(selectedOption)}
          className={`relative w-full overflow-hidden rounded-xl p-5 text-left font-medium transition border ${
            selectedOption === "optionA"
              ? "border-blue-600 bg-blue-50 text-blue-900 shadow-md"
              : selectedOption
              ? "border-gray-200 bg-gray-50 text-gray-400"
              : "border-gray-200 bg-white text-gray-900 hover:border-blue-300 hover:bg-blue-50/50"
          }`}
        >
          {currentResult && (
            <div
              className="absolute inset-y-0 left-0 bg-blue-100/60 transition-all duration-700 ease-out"
              style={{ width: `${currentResult.optionAPct}%` }}
            />
          )}
          <div className="relative z-10 flex items-center justify-between">
            <span className="font-semibold">{currentQuestion.optionA.title}</span>
            {currentResult && (
              <span className="font-bold text-blue-600">{currentResult.optionAPct}%</span>
            )}
          </div>
        </button>

        {/* Option B */}
        <button
          onClick={() => handleSelect("optionB")}
          disabled={Boolean(selectedOption)}
          className={`relative w-full overflow-hidden rounded-xl p-5 text-left font-medium transition border ${
            selectedOption === "optionB"
              ? "border-indigo-600 bg-indigo-50 text-indigo-900 shadow-md"
              : selectedOption
              ? "border-gray-200 bg-gray-50 text-gray-400"
              : "border-gray-200 bg-white text-gray-900 hover:border-indigo-300 hover:bg-indigo-50/50"
          }`}
        >
          {currentResult && (
            <div
              className="absolute inset-y-0 left-0 bg-indigo-100/60 transition-all duration-700 ease-out"
              style={{ width: `${currentResult.optionBPct}%` }}
            />
          )}
          <div className="relative z-10 flex items-center justify-between">
            <span className="font-semibold">{currentQuestion.optionB.title}</span>
            {currentResult && (
              <span className="font-bold text-indigo-600">{currentResult.optionBPct}%</span>
            )}
          </div>
        </button>
      </div>

      {selectedOption && (
        <div className="mt-6">
          <button
            onClick={handleNext}
            className="w-full rounded-xl bg-gray-900 py-3.5 text-white font-semibold hover:bg-black transition"
          >
            {currentIndex + 1 < game.questions.length ? "다음 문항으로" : "결과 보기"}
          </button>
        </div>
      )}
    </div>
  );
}
